import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
  CircularProgress,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LockIcon from '@mui/icons-material/Lock';
import { getGitHubStatus, fetchGitHubRepos, GitHubRepo, GitHubConnectionStatus } from '../api/github';
import { connectGitHub } from '../utils/github';

interface GitHubRepoPickerProps {
  onSelect: (codeUrl: string, codeRef: string) => void;
  value?: string;
}

const GitHubRepoPicker: React.FC<GitHubRepoPickerProps> = ({ onSelect, value }) => {
  const [status, setStatus] = useState<GitHubConnectionStatus | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [manualUrl, setManualUrl] = useState(value || '');
  const [manualRef, setManualRef] = useState('');

  const loadStatus = async () => {
    try {
      const s = await getGitHubStatus();
      setStatus(s);
      if (s.connected) {
        const r = await fetchGitHubRepos();
        setRepos(r);
      }
    } catch {
      setStatus({ connected: false, github_username: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleConnect = async () => {
    try {
      await connectGitHub();
      setLoading(true);
      await loadStatus();
    } catch {
      // User closed popup without authorizing
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {status?.connected ? (
        <Box>
          <Autocomplete
            options={repos}
            getOptionLabel={(option) => option.full_name}
            renderOption={(props, option) => (
              <li {...props} key={option.full_name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.full_name}
                  </Typography>
                  {option.private && (
                    <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                  {option.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 'auto', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            onChange={(_e, repo) => {
              if (repo) {
                onSelect(`https://github.com/${repo.full_name}`, repo.default_branch);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search your repos..."
                size="small"
                fullWidth
              />
            )}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Connected as {status.github_username}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Button
            variant="outlined"
            startIcon={<GitHubIcon />}
            onClick={handleConnect}
            sx={{ mb: 1.5 }}
          >
            Connect GitHub
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="https://github.com/owner/repo"
              value={manualUrl}
              onChange={(e) => {
                setManualUrl(e.target.value);
                onSelect(e.target.value, manualRef);
              }}
              size="small"
            />
            <TextField
              placeholder="main"
              value={manualRef}
              onChange={(e) => {
                setManualRef(e.target.value);
                onSelect(manualUrl, e.target.value);
              }}
              size="small"
              sx={{ width: 120, flexShrink: 0 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GitHubRepoPicker;
